import { Viewer } from "../main";
import {
  DXFFile,
  DXFHandler,
  IFCFile,
  IFCHandler,
  SHPFile,
  SHPHandler
} from ".";
import {
  AmbientLight,
  LineSegments,
  Mesh,
  Scene,
  Points,
  Box3,
  MathUtils,
  TextureLoader,
  Color,
  Vector3,
  BufferGeometry,
  BufferAttribute,
  LineBasicMaterial,
  MeshBasicMaterial,
  Texture,
  Sprite,
  SpriteMaterial,
  LOD,
  Object3D,
  DoubleSide,
  LinearFilter
} from "three";
import { Asset, AssetType, LineworkLayerData } from "../../../redux/assets-slice";
import { mergeBoundingBoxes } from "../utilities";
import { CustomSpriteMaterial } from "../rendering";
import spritePath from "../../textures/sprites/point.png";
import { getBasicPicker, getCustomSpritePicker } from "../gpu-picker";
import LocalScene, { reprojectPoints, webMercator } from "../projections";
import {Feature} from "ol";
import {MultiLineString, MultiPolygon, Point} from "ol/geom";
import {Vector as VectorSource} from "ol/source";
import * as olColor from 'ol/color';
import VectorLayer from "ol/layer/Vector";
import { Layer } from "ol/layer";
import WebGLVectorLayerRenderer from 'ol/renderer/webgl/VectorLayer.js';

const cadPointSize = 10;

export enum GeomType {
  Points = "POINTS",
  Lines = "LINES",
  Polygons = "POLYGONS",
  Text = "TEXT"
}

interface SegmentInfo {
  numPoints: number
  properties: any
}

const validGeomTypes = Object.values(GeomType) as string[];

const pointTexture = new TextureLoader().load(spritePath);

class WebGLLayer extends Layer {
  generateStyle() {
    return {
      symbol: {
        symbolType: 'circle',
        size: cadPointSize,
        color: ['get', 'symbolColor'],
        offset: [0, 0],
        opacity: 0.95,
      },
      'stroke-color': ['get', 'strokeColor'],
      'stroke-width': ['get', 'strokeWidth'],
      'fill-color': ['get', 'fillColor']
    };
  }

  createRenderer() {
    const style = this.generateStyle();
    return new WebGLVectorLayerRenderer(this, {style});
  }
}

export class LineworkPoints extends Points {
  constructor(geometry, material) {
    super(geometry, material);
  }

  updateMaterial(fov, height) {
    const material = this.material as CustomSpriteMaterial;
    material.screenHeight = height;
    material.fov = MathUtils.degToRad(fov);
    material.needsUpdate = true;
  }
}

export class LineworkLine extends LineSegments {
  constructor(geometry, material) {
    super(geometry, material);
  }
}

export class LineworkText extends LOD {
  public text;
  private mesh: Sprite;
  public material: SpriteMaterial;
  public geometry: BufferGeometry;
  public calculated = false;
  private maxDistance = 25;

  private fontSize = 48;
  private lineThickness = 4;
  private borderThickness = 4
  private borderRadius = 4;
  private margin = 5;

  constructor(text, color) {
    super();

    this.material = new SpriteMaterial({
      map: null,
      transparent: false,
      alphaTest: 0.6,
      color,
      opacity: 0.0
    });

    this.mesh = new Sprite(this.material);
    this.geometry = this.mesh.geometry;

    this.addLevel(this.mesh, 0);
    this.addLevel(new Object3D(), this.maxDistance);

    this.text = text;
  }

  drawBackground(ctx, x, y, w, h, r){
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }

  generateTexture(text) {
    const numLines = text.length;
    const canvas = document.createElement('canvas');
    const font = `${this.fontSize}px Arial`;

    const context = canvas.getContext('2d');
    context.font = font;

    // Calculate accurate text width
    let textWidth = 0;
    text.forEach(line => {
      const width = Math.ceil(context.measureText(line).width);
      textWidth = Math.max(textWidth, width);
    });

    // Update canvas width and height with final values
    canvas.height = this.fontSize * (numLines + 0.5) + (2 * this.borderThickness);
    canvas.width =  textWidth + (2 * this.margin) + (2 * this.borderThickness);
    context.font = font;

    if (false) {
      // Draw background
      context.lineWidth = this.borderThickness;
      context.fillStyle = "#000000";
      context.strokeStyle = "#000000";
      this.drawBackground(context,
        this.borderThickness / 2,
        this.borderThickness / 2,
        textWidth + this.borderThickness + 2 * this.margin,
        canvas.height - this.borderThickness,
        this.borderRadius
      );
    }

    // Draw text
    context.lineWidth = this.lineThickness;
    context.fillStyle = "#ffffff";
    context.strokeStyle = "#000000";

    text.forEach((line, i) => {
      const x = this.borderThickness + this.margin;
      const y = (i + 1) * this.fontSize + this.margin;

      context.strokeText(line, x, y);
      context.fillText(line, x, y);
    });

    const xScale = canvas.width / canvas.height;
    const textScale = new Vector3(xScale, 1.0, 1.0)
      .multiplyScalar(numLines)
      .divideScalar(3);

    let texture = new Texture(canvas);
    texture.flipY = true;
    texture.needsUpdate = true;
    texture.minFilter = LinearFilter;
    texture.magFilter = LinearFilter;

    return {texture, textScale};
  }

  updateTexture() {
    const {texture, textScale} = this.generateTexture(this.text);
    this.mesh.scale.copy(textScale);

    const material = this.mesh.material;
    material.opacity = 1.0;
    material.map = texture;
    material.needsUpdate = true;

    this.calculated = true;
  }
}

export class LineworkPolygon extends Mesh {
  constructor(geometry, material) {
    super(geometry, material);
  }
}

export class LineworkLayer {
  public id: string;
  public visible: boolean;
  public name: string;
  public file: LineworkFile
  protected color: string;
  protected enabled = true;

  public mapLayers = [];
  public standardMeshes = [];
  public pickingMeshes = [];
  public features = [];

  constructor(file: LineworkFile, layer: LineworkLayerData) {
    this.file = file;
    this.id = layer.id;
    this.visible = layer.visible;
    this.name = layer.name;
    this.color = layer.color;
  }

  get scene() {
    return this.file.scene;
  }

  get map() {
    return this.file.map;
  }

  get picker() {
    return this.file.picker;
  }

  get viewer() {
    return this.file.viewer;
  }

  get meshes() {
    return [...this.standardMeshes, ...this.pickingMeshes];
  }

  get mapSource() {
    return this.file.mapSource;
  }

  get mapLayer() {
    return this.file.mapLayer;
  }

  get meshColor() {
    return new Color(parseInt(this.color.slice(1), 16));
  }

  get styleColor() {
    return this.color;
  }

  /** @private */
  getCoordinateArray = (segments) => {
    let coordinates = [];
    let segmentInfo: SegmentInfo[] = [];

    // Generate combined coordinates array + segment lengths
    segments.forEach(segment => {
      segmentInfo.push({
        numPoints: segment.coordinates.length,
        properties: segment.properties
      } as SegmentInfo);

      segment.coordinates.forEach(coordinate => {
        coordinates.push(coordinate);
      });
    });

    return {coordinates, segmentInfo};
  }

  async addNewGeometry(data) {
    for (let type of Object.keys(data)) {
      if (!validGeomTypes.includes(type)) continue;

      const geomType = type as GeomType;
      const features = data[geomType];

      if (geomType === GeomType.Text) {
        // TODO: how to make this async? OffscreenCanvas isnt
        // supported in all browsers so a webworker wont help
        this.addTextData(features);
      } else {
        const {coordinates, segmentInfo} = this.getCoordinateArray(features);
        await this.addSceneData(coordinates, segmentInfo, geomType);
        await this.addAerialData(coordinates, segmentInfo, geomType);
      }
    }
  }

  /** @private */
  addTextData(features) {
    features.forEach(feature => {
      const {coordinates, properties} = feature;
      const offset = new Vector3(...coordinates[0]);
      const text = properties.text;

      const shpMesh = new LineworkText(text, this.meshColor);

      // Convert offset to meters
      offset.multiplyScalar(LocalScene.dataToMeters);

      // Move model to scene coordinates
      const shift = LocalScene.offsetToScene(offset);
      shpMesh.position.copy(shift);

      if (!this.enabled) return;

      // Scene view mesh
      this.standardMeshes.push(shpMesh);
      this.scene.add(shpMesh);

      // Update visibility
      shpMesh.visible = this.visible;
    });
  }

  /** @private */
  async addAerialData(coordinates, segmentInfo: SegmentInfo[], geomType: GeomType) {
    if (coordinates.length === 0) return;
    if (!validGeomTypes.includes(geomType)) return;

    // Reproject points to web mercator
    const reprojected = await reprojectPoints(
      coordinates, LocalScene.dataProjection, webMercator);

    let mapCoordinates;
    let geometries = [];

    if (geomType === GeomType.Points) {
      // WebGL layer doesnt like MultiPoint, so we use Point instead
      reprojected.forEach(coordinate => {
        let geometry = new Point(coordinate);
        geometries.push(geometry);
      });
    } else if (geomType === GeomType.Polygons) {
      let polygonRings = [];
      const numFaces = reprojected.length / 3;
      for (let i=0; i<numFaces; i++) {
        const slice = reprojected.slice(3*i, 3*(i+1));
        polygonRings.push([slice]);
      }

      geometries.push(new MultiPolygon(polygonRings, "XY"));
    } else if (geomType === GeomType.Lines) {
      let startIndex = 0;
      mapCoordinates = [];
      segmentInfo.forEach(segmentInfo => {
        const {numPoints} = segmentInfo;
        const endIndex = startIndex + numPoints;
        const slice = reprojected.slice(startIndex, endIndex);
        mapCoordinates.push(slice);
        startIndex = endIndex;
      });

      geometries.push(new MultiLineString(mapCoordinates, "XY"));
    }

    const features = geometries.map(geometry => {
      const feature = new Feature({geometry});
      feature.set("geomType", geomType);
      this.setFeatureColor(feature);
      return feature;
    });

    this.features = [...this.features, ...features];
    if (!this.visible) return;

    this.mapSource.addFeatures(features);
  }

  /** @private */
  async addSceneData(coordinates, segmentInfo: SegmentInfo[], geomType: GeomType) {
    if (coordinates.length === 0) return;

    // Not required for 2D data
    if (coordinates[0].length < 3) return;

    // Subtract offset to handle float32 issues
    const flatCoordinates = [];
    const offset = new Vector3(...coordinates[0]);
    coordinates.forEach(coordinate => {
      // Make sure height exists to avoid errors
      if (coordinate.length === 2) {
        coordinate.push(0);
      }

      flatCoordinates.push(
        coordinate[0] - offset.x,
        coordinate[1] - offset.y,
        coordinate[2] - offset.z
      );
    });

    const bufferGeometry = new BufferGeometry();
    const positions = new Float32Array(flatCoordinates);
    const gpuIndex = new Int32Array(flatCoordinates.length / 3);

    bufferGeometry.setAttribute('position', new BufferAttribute(positions, 3));
    bufferGeometry.setAttribute('gpu_index', new BufferAttribute(gpuIndex, 1));
    bufferGeometry.computeBoundingSphere();

    let material;
    let shpMesh;
    let pickerMesh;

    if (geomType === GeomType.Points) {
      // Create material and mesh objects
      material = new CustomSpriteMaterial({
        map: pointTexture,
        size: 0.10,
        gpuPicker: false,
        color: this.meshColor
      });

      shpMesh = new LineworkPoints(bufferGeometry, material);
      pickerMesh = getCustomSpritePicker(shpMesh);
    } else if (geomType === GeomType.Lines) {
      // Set geometry indices for line segments
      const bufferIndices = this.getLineIndices(segmentInfo);
      bufferGeometry.setIndex(bufferIndices);

      // Create material and mesh objects
      material = new LineBasicMaterial({
        color: this.meshColor
      });

      shpMesh = new LineworkLine(bufferGeometry, material);
      pickerMesh = getBasicPicker(shpMesh, LineSegments);
    } else if (geomType === GeomType.Polygons) {
      // Create material and mesh objects
      material = new MeshBasicMaterial({
        color: this.meshColor,
        side: DoubleSide
      });

      shpMesh = new Mesh(bufferGeometry, material);
      pickerMesh = getBasicPicker(shpMesh, Mesh);
    } else {
      return;
    }

    // Convert mesh to meters
    shpMesh.scale.setScalar(LocalScene.dataToMeters);
    pickerMesh.scale.setScalar(LocalScene.dataToMeters);
    offset.multiplyScalar(LocalScene.dataToMeters);

    // Move model to scene coordinates
    const shift = LocalScene.offsetToScene(offset);
    shpMesh.position.copy(shift);
    pickerMesh.position.copy(shift);

    if (!this.enabled) return;

    // Scene view mesh
    this.standardMeshes.push(shpMesh);
    this.scene.add(shpMesh);

    // Picking scene mesh
    this.pickingMeshes.push(pickerMesh);
    this.picker.add(pickerMesh);

    // Update visibility
    shpMesh.visible = this.visible;
    pickerMesh.visible = this.visible;
  }

  /** Generate bufferGeometry index values for line segments  */
  getLineIndices(segmentInfo: SegmentInfo[]) {
    let geomIndex = 0;
    let bufferIndices = [];

    segmentInfo.forEach(segmentInfo => {
      const {numPoints} = segmentInfo;

      for (let i=0; i < numPoints-1; i++) {
        bufferIndices.push(geomIndex, geomIndex + 1);
        geomIndex = geomIndex + 1;
      }

      geomIndex = geomIndex + 1;
    });

    return bufferIndices;
  }

  removeFromScene(item) {
    if (!item) return;
    this.picker.remove(item);
    this.scene.remove(item);
  }

  disposeGeometry(item) {
    if (!item) return;
    item.geometry.dispose();
    item.material.dispose();
  }

  setVisibility(state) {
    if (state === this.visible) return;

    this.visible = state;

    this.features.forEach(feature => {
      try {
        if (this.visible) {
          this.mapSource.addFeature(feature);
        } else {
          this.mapSource.removeFeature(feature);
        }
      } catch(err) {
        // Feature has already been added/removed
        return;
      }
    });

    this.standardMeshes.forEach(standardMesh => {
      standardMesh.visible = state;
    });

    this.pickingMeshes.forEach(pickingMesh => {
      pickingMesh.visible = state;
    });
  }

  setFeatureColor(feature) {
    const geomType = feature.get("geomType");
    feature.set("symbolColor", this.styleColor);

    if (geomType === GeomType.Polygons) {
      // Fill color has 50% opacity
      feature.set("fillColor", withOpacity(this.styleColor, 0.5));
      feature.set("strokeColor", "#000000");
      feature.set("strokeWidth", 1.0);
    } else {
      feature.set("fillColor", this.styleColor);
      feature.set("strokeColor", this.styleColor);
      feature.set("strokeWidth", 2.0);
    }
  }

  setColor(color) {
    this.color = color;

    this.standardMeshes.forEach(mesh => {
      const material = mesh.material;
      material.color = this.meshColor;
      material.needsUpdate = true;
    });

    this.features.forEach(feature => {
      this.setFeatureColor(feature);
    });
  }

  destroy() {
    this.enabled = false;

    this.standardMeshes.forEach(standardMesh => {
      this.removeFromScene(standardMesh);
      this.disposeGeometry(standardMesh);
    });
    this.standardMeshes = [];

    this.pickingMeshes.forEach(pickingMesh => {
      this.removeFromScene(pickingMesh);
      this.disposeGeometry(pickingMesh);
    });
    this.pickingMeshes = [];

    this.features.forEach(feature => {
      try {
        this.mapSource.removeFeature(feature);
      } catch(err) {
        // Feature has already been removed
        return;
      }
    });
    this.features = [];
  }
}

export class LineworkFile {
  public id: string;
  protected path: string;
  protected name: string;
  protected saved: boolean
  public boundingBox: Box3;
  public mapLayer: VectorLayer<any> | WebGLLayer;
  public mapSource: VectorSource<any>;

  public loaded = false;
  public enabled = true;
  public loading = false;
  public visible = false;
  public percent = 0.0;
  public error = false;

  protected handler: IFCHandler | SHPHandler | DXFHandler;

  constructor(handler, asset: Asset) {
    this.id = asset.id;
    this.path = asset.path;
    this.name = asset.name;
    this.saved = asset.saved;
    this.handler = handler;

    const source = new VectorSource({
      wrapX: false,
      features: [],
    });

    const layer = new WebGLLayer({
      zIndex: 1,
      source: source,
    });

    this.mapLayer = layer;
    this.mapSource = source;
    this.map.addLayer(layer);
  }

  get viewer() {
    return this.handler.viewer;
  }

  get scene() {
    return this.handler.scene;
  }

  get map() {
    return this.handler.map;
  }

  get picker() {
    return this.handler.picker;
  }

  load() {
    console.warn("Not Implemented");
  }

  destroy() {
    console.warn("Not Implemented");
  }

  removeMapLayer() {
    if (!this.mapLayer) return;
    this.map.removeLayer(this.mapLayer);
  }

  updateProperties(asset: Asset) {
    console.warn("Not Implemented");
  }

  updatePercent(percent) {
    this.percent = percent;
  }

  updatePicker() {
    this.picker.needsUpdate = true;
  }

  validBounds(bounds: {min: number[], max: number[]}) {
    return [...bounds.min, ...bounds.max]
      .filter(x => isFinite(x)).length === 6;
  };
}

export class LineworkHandler {
  protected files: LineworkFile[] = [];
  protected cadHandler: CADHandler;

  constructor(cadHandler: CADHandler) {
    this.cadHandler = cadHandler;
  }

  get viewer() {
    return this.cadHandler.viewer;
  }

  get camera() {
    return this.viewer.camera;
  }

  get map() {
    return this.viewer.minimap.map;
  }

  get scene() {
    return this.cadHandler.scene;
  }

  get picker() {
    return this.cadHandler.picker;
  }

  get numberOfFiles() {
    return this.files.length;
  }

  get combinedBoundingBox() {
    let boundingBox = null;
    this.files.forEach(file => {
      boundingBox = mergeBoundingBoxes(boundingBox, file.boundingBox);
    });

    return boundingBox;
  }

  get objects() {
    console.warn("Not implemented");
    return [];
  }

  getByID(assetID) {
    return this.files.find(file => file.id === assetID);
  }

  addNewFile(asset, FileConstructor) {
    let assetID = asset.id;
    if (this.getByID(assetID)) return false;

    const file = new FileConstructor(this, asset);
    this.files.push(file);
  }

  deleteFile(asset) {
    let file = this.getByID(asset.id);
    if (!file) return;

    file.destroy();
    const index = this.files.indexOf(file);
    this.files.splice(index, 1);
  }

  async getProperties(event) {
    console.warn("Not Implemented");
    return [];
  }

  clearHighlight() {
    console.warn("Not Implemented");
  }

  updateProperties(asset) {
    let file = this.getByID(asset.id);
    if (!file) return;

    file.updateProperties(asset);
  }

  reconcile(modelFiles, FileConstructor) {
    const currentFiles = new Set(this.files.map(ifc => ifc.id));
    const newFiles = new Set(modelFiles.map(ifc => ifc.id));
    const filesToLoad = modelFiles.filter(ifc => !currentFiles.has(ifc.id));
    const filesToDelete = this.files.filter(ifc => !newFiles.has(ifc.id));

    // Delete file that need to be removed
    filesToDelete.forEach(file => {
      this.deleteFile(file);
    });

    // Add new files
    filesToLoad.forEach(file => {
      this.addNewFile(file, FileConstructor);
    });

    // Toggle visibility and colors for each file
    modelFiles.forEach(file => {
      this.updateProperties(file);
    });

    return filesToLoad.length;
  }

  updateMeshes() {
    let meshes = [];
    this.files.forEach(file => {
      if ((file instanceof SHPFile) || (file instanceof DXFFile)) {
        file.layers.forEach(layer => {
          meshes = [...meshes, ...layer.meshes];
        });
      }
    });

    // Update point sizing
    const points = meshes.filter(mesh => mesh instanceof LineworkPoints);

    points.forEach((mesh: LineworkPoints) => {
      mesh.updateMaterial(
        this.camera.fov,
        this.viewer.height
      );
    });

    // Update textures for text objects
    let text = meshes
      .filter(mesh => mesh instanceof LineworkText)
      .filter(mesh => !mesh.calculated)
      .filter(mesh => mesh.getCurrentLevel() === 0)
      .slice(0, 10);

    text.forEach((mesh: LineworkText) => {
      mesh.updateTexture();
    });
  }

  /** Load any file waiting for conversion */
  loadNextfile() {
    for (let file of this.files) {
      if (file.loaded || !file.enabled || !file.visible) continue;
      if (file.loading) break;

      // Load new file
      file.load();
      break;
    };
  }

  update() {
    this.loadNextfile();
    this.updateMeshes();
  }
}

export class CADHandler {
  public scene: Scene;
  public viewer: Viewer;
  private ifcHandler: IFCHandler
  private shpHandler: SHPHandler
  private dxfHandler: DXFHandler

  constructor(viewer: Viewer) {
    this.viewer = viewer;

    this.initScene();
    this.ifcHandler = new IFCHandler(this);
    this.shpHandler = new SHPHandler(this);
    this.dxfHandler = new DXFHandler(this);
  }

  get picker() {
    return this.viewer.gpuPickers.default;
  }

  get handlers() {
    return [
      this.ifcHandler,
      this.shpHandler,
      this.dxfHandler
    ];
  }

  get numberOfFiles() {
    return this.handlers
      .map(x=> x.numberOfFiles)
      .reduce((previous, current) => previous + current, 0);
  }

  get combinedBoundingBox() {
    let boundingBox = null;
    this.handlers.map(x => x.combinedBoundingBox).forEach(bbox => {
      boundingBox = mergeBoundingBoxes(boundingBox, bbox);
    });

    return boundingBox;
  }

  initScene() {
    this.scene = new Scene();
    this.scene.add(new AmbientLight(0xFFFFFF));
  }

  getByID(id) {
    let file;
    this.handlers.forEach(handler => {
      if (file) return;
      file = handler.getByID(id);
    });

    return file;
  }

  async getProperties(event) {
    let properties = [];

    for (let handler of this.handlers) {
      const attributes = await handler.getProperties(event);
      properties = [
        ...properties,
        ...attributes
      ];
    }

    return properties;
  }

  clearHighlight() {
    for (let handler of this.handlers) {
      handler.clearHighlight();
    }
  }

  reconcile(modelFiles) {
    let numAdded = 0;

    // Add/remove ifc files
    const ifcFiles = modelFiles.filter(x => x.type === AssetType.IFC);
    numAdded += this.ifcHandler.reconcile(ifcFiles, IFCFile);

    // Add/remove shp files
    const shpFiles = modelFiles.filter(x => x.type === AssetType.SHP);
    numAdded += this.shpHandler.reconcile(shpFiles, SHPFile);

    // Add/remove dxf files
    const dxfFiles = modelFiles.filter(x => x.type === AssetType.DXF);
    numAdded += this.dxfHandler.reconcile(dxfFiles, DXFFile);

    return numAdded;
  }

  update() {
    this.handlers.forEach(handler => {
      handler.update();
    });
  }
}

const withOpacity = (color, opacity) => {
  let colorWithOpacity = olColor.asArray(color).slice();
  colorWithOpacity[3] = opacity;
  return colorWithOpacity;
};